import { useMemo } from 'react';
import useTranslation from 'next-translate/useTranslation';

import { useIsMobile } from '~/domains/common/hooks/mediaQueries';
import { Tabs } from '~/domains/design-system/Tabs';
import dt from '~/testing';
import { UserContestAssociation } from '~/domains/contest/domains/services/enums/user-contest-association.enum';
import { URLQueryKeys } from '~/domains/common/types/URLQueryKeys';
import { ChannelContestFilter } from '~/domains/channel/enums';
import getIsEnteredTabShown from '~/domains/channel/utils/getIsEnteredTabShown';

import useStyles from './ChannelNavigation.styles';

function ChannelNavigation({
  isCurrentUserChannel,
  isSplashAccount,
}: {
  isCurrentUserChannel: boolean;
  isSplashAccount?: boolean;
}) {
  const { t } = useTranslation('channel');
  const isMobile = useIsMobile();
  const { classes } = useStyles({ isMobile });

  const isEnteredTabShown = getIsEnteredTabShown({ isCurrentUserChannel, isSplashAccount });

  const associationTabs = useMemo(
    () => [
      {
        label: t('contests.tabs.created'),
        value: UserContestAssociation.CREATED,
        'data-test-id': dt.channel.connectedChannelHeader.createdTab,
      },
      ...(isEnteredTabShown
        ? [
            {
              label: t('contests.tabs.entered'),
              value: UserContestAssociation.ENTERED,
              'data-test-id': dt.channel.connectedChannelHeader.enteredTab,
            },
          ]
        : []),
    ],
    [isEnteredTabShown, t]
  );

  const filterTabs = useMemo(
    () => [
      {
        label: t('contests.filters.all'),
        value: ChannelContestFilter.ALL,
        'data-test-id': dt.channel.connectedChannelHeader.allButton,
      },
      {
        label: t('contests.filters.notYetStarted'),
        value: ChannelContestFilter.NOT_YET_STARTED,
        'data-test-id': dt.channel.connectedChannelHeader.upcomingButton,
      },
      {
        label: t('contests.filters.started'),
        value: ChannelContestFilter.STARTED,
        'data-test-id': dt.channel.connectedChannelHeader.liveButton,
      },
      {
        label: t('contests.filters.final'),
        value: ChannelContestFilter.FINAL,
        'data-test-id': dt.channel.connectedChannelHeader.recentButton,
      },
    ],
    [t]
  );

  return (
    <>
      {associationTabs.length > 1 && (
        <Tabs
          id={URLQueryKeys.ASSOCIATION}
          grow={isMobile}
          tabs={associationTabs}
          // @ts-expect-error - SX type incompatible with styles
          sx={() => classes.tabOverride}
        />
      )}
      {isCurrentUserChannel && (
        <Tabs
          data-test-id={dt.channel.connectedChannelHeader.contestStatusBar}
          id={URLQueryKeys.CONTEST_FILTER}
          tabs={filterTabs}
          classNames={{
            tabsList: classes.pillsList,
            tab: classes.pill,
          }}
        />
      )}
    </>
  );
}

export default ChannelNavigation;
